import { isNoDoerAssignedError } from '@doneverse/data/api'
import { workspaceDetailedOptions } from '@doneverse/data/queries'
import { useQuery } from '@tanstack/react-query'
import { useRouter, Outlet } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useAuthenticatedUser } from '@/components/auth/AuthenticatedUserContext'
import { RouteErrorBoundary } from '@/components/error/RouteErrorBoundary'
import { PageLayout } from '@/components/layout/PageLayout'
import { NoDoerAssignedState } from '@/components/playbook/NoDoerAssignedState'

export function PlaybookAccessErrorBoundary({ error }: { error: unknown }) {
  const { profile } = useAuthenticatedUser()
  const router = useRouter()
  const isFounder = profile.role === 'founder'
  const shouldShowNoDoerState = isFounder && isNoDoerAssignedError(error)

  const {
    isSuccess: recoverySucceeded,
    refetch: refetchWorkspace,
    fetchStatus: recoveryFetchStatus,
  } = useQuery({
    ...workspaceDetailedOptions(),
    enabled: isFounder,
  })

  useEffect(() => {
    if (!shouldShowNoDoerState) return
    if (!recoverySucceeded) return
    void router.invalidate({ sync: false })
  }, [recoverySucceeded, router, shouldShowNoDoerState])

  if (shouldShowNoDoerState) {
    return (
      <PageLayout title="Playbooks">
        <NoDoerAssignedState
          className="min-h-[360px] flex flex-col items-center justify-center"
          onRetry={() => {
            void refetchWorkspace()
          }}
          retrying={recoveryFetchStatus === 'fetching'}
        />
      </PageLayout>
    )
  }

  return <RouteErrorBoundary error={error} />
}

export function PlaybookAccessLayout() {
  const { profile } = useAuthenticatedUser()
  const router = useRouter()
  const isFounder = profile.role === 'founder'
  const {
    error: workspaceError,
    refetch: refetchWorkspace,
    fetchStatus: workspaceFetchStatus,
    isSuccess: workspaceLoaded,
  } = useQuery({
    ...workspaceDetailedOptions(),
    enabled: isFounder,
  })
  const hadNoDoerStateRef = useRef(false)
  const shouldShowNoDoerState = isFounder && isNoDoerAssignedError(workspaceError)

  useEffect(() => {
    if (shouldShowNoDoerState) {
      hadNoDoerStateRef.current = true
    }
  }, [shouldShowNoDoerState])

  useEffect(() => {
    if (!shouldShowNoDoerState) return

    const handleVisibilityRecovery = () => {
      void refetchWorkspace()
    }

    document.addEventListener('visibilitychange', handleVisibilityRecovery)
    window.addEventListener('focus', handleVisibilityRecovery)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityRecovery)
      window.removeEventListener('focus', handleVisibilityRecovery)
    }
  }, [refetchWorkspace, shouldShowNoDoerState])

  useEffect(() => {
    if (!hadNoDoerStateRef.current || !workspaceLoaded) return

    hadNoDoerStateRef.current = false
    void router.invalidate({ sync: false })
  }, [router, workspaceLoaded])

  if (shouldShowNoDoerState) {
    return (
      <PageLayout title="Playbooks">
        <NoDoerAssignedState
          className="min-h-[360px] flex flex-col items-center justify-center"
          onRetry={() => {
            void refetchWorkspace()
          }}
          retrying={workspaceFetchStatus === 'fetching'}
        />
      </PageLayout>
    )
  }

  return <Outlet />
}
